import React from "react";
import * as M from "../../Modal";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import { T, TC } from "../../../Constants";

export type RiskMapperProps = {
    /** The id of the remarque */
    remarque_id: string;
    /** The categories configured for the mission */
    categories: T.Mission["nen2767"]["categories"];
    /** Callback to set the level */
    on_set_level: (category_id: string, level: number) => void;
}

export type RiskMapperControlledProps = Omit<RiskMapperProps, "on_set_level" | "categories"> & {
    /** The default categories to be repopulated */
    default_categories: RiskMapperProps["categories"];
    /** Callback to validate all the changes */
    on_validate: (categories: RiskMapperProps["categories"]) => void;
    /** Display the form in a popup ? */
    popup?: boolean;
    /** Style to change the modal */
    modal?: M.StyleModalProps;
}

const LEVELS = [
    { num: 0, color: "bg-success" },
    { num: 1, color: "bg-gold" },
    { num: 2, color: "bg-warning" },
    { num: 3, color: "bg-danger" },
];

export const RiskMapper: React.FC<RiskMapperProps> = ({ on_set_level, ...props }) => {
    return <div>
        <C.Title level={4} text={TC.NEN_RISK_MATRIX_CATEGORY_NAME} />

        {props.categories.map(cat => <BS.Row key={cat.id} className="mb-2 g-1">
            <BS.Col xs={6} children={cat.name} />

            {LEVELS.map(level => <BS.Col
                xs={1}
                key={level.num}
                className="text-center pointer text-white"
                onClick={() => on_set_level(cat.id, level.num)}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                onMouseLeave={(e) => {
                    if (cat?.remarques?.[props.remarque_id] !== level.num) e.currentTarget.style.opacity = "0.5";
                }}
                style={{
                    transition: "opacity 0.2s",
                    opacity: cat?.remarques?.[props.remarque_id] === level.num ? 1 : 0.5,
                }}
            >
                <div
                    children={level.num}
                    className={level.color}
                    style={{ border: cat?.remarques?.[props.remarque_id] === level.num ? "1px solid black" : "1px solid transparent" }}
                />
            </BS.Col>)}
        </BS.Row>)}

    </div>;
}

export const RiskMapperControlled: React.FC<RiskMapperControlledProps> = ({ on_validate, default_categories, ...props }) => {
    const [categories, setCategories] = React.useState<RiskMapperProps["categories"]>(default_categories || []);

    const set_level = React.useCallback<RiskMapperProps["on_set_level"]>((category, level) => {
        setCategories(categories => categories.map(cat => cat.id === category ? { ...cat, remarques: { ...cat.remarques, [props.remarque_id]: level } } : cat));
    }, []);

    const footer = React.useMemo(() => <C.Flex justifyContent="end" className="mt-2">
        <C.Button
            icon="save"
            text={TC.GLOBAL_CONFIRM}
            onClick={() => on_validate(categories)}
        />
    </C.Flex>, [on_validate, categories]);

    return React.createElement(
        props.popup ? M.BlankModal : React.Fragment,
        props.popup ? {
            ...props.modal,
            footer: footer,
            size: props.modal?.size || "md",
            onQuit: () => on_validate?.(null),
            title: props.modal?.title || TC.NEN_RISK_MATRIX_CATEGORY_NAME,
        } as M.BlankModalProps : null,
        <>
            <RiskMapper {...props} categories={categories} on_set_level={set_level} />
            {!props.popup && footer}
        </>,
    );
}